import Box from "@pagerland/common/src/components/Box";
import Button from "@pagerland/common/src/components/Button";
import Card from "@pagerland/common/src/components/Card";
import Container from "@pagerland/common/src/components/Container";
import Grid from "@pagerland/common/src/components/Grid";
import Img from "gatsby-image";
import Typography from "@pagerland/common/src/components/Typography";
import TagAlt from "@pagerland/icons/src/line/TagAlt";
import PropTypes from "prop-types";
import React from "react";
import Fade from "react-reveal/Fade";
import styled from "styled-components";
import data from "../../data";

const CourseCard = styled(Card)`
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  &:hover,
  &:focus-within {
    transform: translateY(-5px);
    box-shadow: 0 16px 30px rgba(31, 24, 14, 0.14);
    border-color: rgba(31, 35, 40, 0.16);
  }
`;

export default function MontessoriCourses({
  name,
  data,
  title,
  header,
  WrapperProps,
  ContainerProps,
  TitleProps,
  HeaderProps,
  GridProps,
  CardProps,
  ImgProps,
  CaptionProps,
  ItemTitleProps,
  CtaProps,
}) {
  const properties = Array.isArray(data) ? data : (data && data.nodes) || [];

  return (
    <Box name={name} {...WrapperProps}>
      <Container {...ContainerProps}>
        <Typography {...TitleProps}>{title}</Typography>
        <Typography {...HeaderProps}>{header}</Typography>

        <Grid {...GridProps}>
          {properties.map((property, key) => {
            const slug = property.frontmatter.slug;
            const ctaLabel = property.serviceCta || "Learn more";
            const courseHref = `/course/${slug}`;
            return (
              <Fade key={key} cascade bottom duration={600} delay={100 * key}>
                <CourseCard {...CardProps}>
                  <Img
                    {...ImgProps}
                    fluid={property.frontmatter.thumb.childImageSharp.fluid}
                  />
                  <Box {...CaptionProps}>
                    <Typography {...ItemTitleProps}>{property.frontmatter.title}</Typography>
                    <Button {...CtaProps} as="a" href={courseHref}>
                      {ctaLabel}
                    </Button>
                  </Box>
                </CourseCard>
              </Fade>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}

MontessoriCourses.propTypes = {
  name: PropTypes.string.isRequired,
  data: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.shape({
      nodes: PropTypes.arrayOf(PropTypes.object),
    }),
  ]),
  title: PropTypes.node,
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.node,
      price: PropTypes.node,
      features: PropTypes.arrayOf(
        PropTypes.shape({
          IconProps: PropTypes.object,
          title: PropTypes.node,
        })
      ),
      LinkProps: PropTypes.object,
      ImgProps: PropTypes.object,
    })
  ),
  WrapperProps: PropTypes.object,
  ContainerProps: PropTypes.object,
  TitleProps: PropTypes.object,
  HeaderProps: PropTypes.object,
  GridProps: PropTypes.object,
  CardProps: PropTypes.object,
  ImgProps: PropTypes.object,
  CaptionProps: PropTypes.object,
  PriceBadgeProps: PropTypes.object,
  PriceBadgeIconProps: PropTypes.object,
  ItemTitleProps: PropTypes.object,
  CtaProps: PropTypes.object,
  FeaturesGridProps: PropTypes.object,
  FeatureProps: PropTypes.object,
  FeatureIconProps: PropTypes.object,
  FeatureTitleProps: PropTypes.object,
};

MontessoriCourses.defaultProps = {
  ...data.montessoriItems,
  WrapperProps: {
    borderTopWidth: "1px",
    borderTopColor: "rgba(31, 35, 40, 0.1)",
    bg: "#fcfaf6",
    pt: {
      _: 48,
      lg: 56,
    },
    pb: {
      _: 64,
      md: 72,
      lg: 82,
    },
  },
  TitleProps: {
    pt: 0,
    pb: {
      _: 2,
      md: 3,
      lg: 4,
    },
    as: "h2",
    variant: "h2",
    lineHeight: 1.12,
    letterSpacing: "-0.02em",
  },
  HeaderProps: {
    pt: 0,
    pb: {
      _: 3,
      md: 4,
      lg: 5,
    },
    as: "h5",
    maxWidth: 760,
    color: "rgba(30, 30, 30, 0.74)",
  },
  GridProps: {
    gridTemplateColumns: {
      _: "1fr",
      lg: "repeat(3, 2fr)",
    },
    gridGap: "28px",
  },
  CardProps: {
    p: 0,
    bg: "#ffffff",
    borderRadius: 10,
    overflow: "hidden",
    display: "block",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "rgba(31, 35, 40, 0.09)",
    boxShadow: "0 10px 24px rgba(31, 24, 14, 0.08)",
    height: "100%",
  },
  ImgProps: {
    width: "100%",
    display: "block",
    style: {
      height: 228,
    },
    imgStyle: {
      objectFit: "cover",
    },
  },
  CaptionProps: {
    py: 18,
    px: 3,
    display: "flex",
    flexDirection: "column",
    minHeight: 160,
  },
  PriceBadgeProps: {
    position: "absolute",
    left: 0,
    top: 16,
  },
  PriceBadgeIconProps: {
    icon: TagAlt,
    fontSize: 24,
  },
  ItemTitleProps: {
    as: "h3",
    variant: "h4",
    color: "#000",
    mb: 2,
    lineHeight: 1.3,
  },
  CtaProps: {
    variant: "brand",
    size: "small",
    mt: "auto",
    width: "auto",
    alignSelf: "flex-start",
    borderRadius: 6,
    px: 3,
    color: "gray.0",
    fontWeight: 700,
    as: "a",
  },
  FeaturesGridProps: {
    mt: 4,
    maxWidth: 320,
    gridTemplateColumns: "repeat(2, 1fr)",
    gridColumnGap: "32px",
    gridRowGap: "8px",
  },
  FeatureProps: {
    flexBox: true,
    alignItems: "center",
  },
  FeatureIconProps: {
    fontSize: 24,
    mr: 2,
    color: "#000",
  },
  FeatureTitleProps: {
    variant: "small",
    color: "gray.1",
  },
};

//export default MontessoriCourses;
